import { Component, computed, input, signal, ViewChild } from '@angular/core';
import { ModalWindowComponent } from '../../../modal-window/modal-window.component';
import { UntilDestroy } from '@ngneat/until-destroy';
import { UpdateAgencyLeadModalService } from './update-agency-lead-modal.service';
import {
  Agency,
  AGENCY_TYPE,
  AgencyKeys,
  AGENT_TYPE,
  AGENT_TYPE_LOOKUP,
  AgentKeys,
  ElasticSearchAgent,
  LookupKeys,
  TRUE_FALSE_LOOKUP,
} from '@ag-common-lib/public-api';
import { AgentSearchService } from 'ag-common-svc/lib/services/agent-search';
import { AgencyService } from 'ag-common-svc/public-api';
import { QueryParam, WhereFilterOperandKeys } from 'ag-common-svc/lib/dao/CommonFireStoreDao.dao';
import { firstValueFrom, take } from 'rxjs';
import { DxFilterOperators } from '@ag-common-lib/lib';
import { BaseModelKeys } from '@ag-common-lib/lib/models/base.model';
import { SelectionChangedEvent } from 'devextreme/ui/data_grid';
import { DxDropDownBoxComponent } from 'devextreme-angular';
import { validateDxGroups } from 'ag-common-svc/lib/utils/validation';

@UntilDestroy()
@Component({
  selector: 'ag-shr-update-agency-lead-modal',
  templateUrl: './update-agency-lead-modal.component.html',
  styleUrls: ['./update-agency-lead-modal.component.scss'],
})
export class UpdateAgencyLeadModalComponent {
  @ViewChild('updateAgencyLeadModalRef', { static: true }) updateAgencyLeadModalModalComponent: ModalWindowComponent;

  agencySig = input.required<Agency>();
  protected formDataSig = this.updateAgencyLeadModalService.formDataSig;
  protected inProgress$ = this.updateAgencyLeadModalService.inProgress$;
  protected AgencyKeys = AgencyKeys;
  protected AgentKeys = AgentKeys;
  protected AGENCY_TYPE = AGENCY_TYPE;
  protected LookupKeys = LookupKeys;
  readonly TRUE_FALSE_LOOKUP = TRUE_FALSE_LOOKUP;
  protected AGENT_TYPE_LOOKUP = AGENT_TYPE_LOOKUP;
  protected readonly validationGroup = 'agencyLeadValidationGroup';

  protected modalTitleSig = computed(() => {
    const agency = this.agencySig();
    const agencyLead = agency?.[AgencyKeys.agencyLead];
    const agencyName = agency?.[AgencyKeys.name];

    const nameParts = [];

    if (agencyName) {
      nameParts.push(agencyName);
    }

    if (agencyLead) {
      nameParts.push('Change Lead');
      return nameParts.join(': ');
    }

    nameParts.push('Assign Lead');

    return nameParts.join(': ');
  });

  protected defaultFilterSig = computed(() => {
    const agency = this.agencySig();
    const agencyLead = agency?.[AgencyKeys.mga];
    const agencyId = agency?.[AgencyKeys.agencyId];

    return [
      [AgentKeys.is_manager, DxFilterOperators.equal, true],
      DxFilterOperators.and,
      [AgentKeys.p_agency_id, DxFilterOperators.equal, agencyId],
      DxFilterOperators.and,
      [
        [AgentKeys.agent_type, DxFilterOperators.equal, AGENT_TYPE.AGENCY_ADMIN],
        DxFilterOperators.or,
        [AgentKeys.agent_type, DxFilterOperators.equal, AGENT_TYPE.AGENCY_PRINCIPAL],
        DxFilterOperators.or,
        [AgentKeys.agent_type, DxFilterOperators.equal, AGENT_TYPE.MGA_ADMIN],
        DxFilterOperators.or,
        [AgentKeys.agent_type, DxFilterOperators.equal, AGENT_TYPE.MGA_PRINCIPAL],
      ],
    ];
  });

  protected agentsDataSource = this.agentSearchService.getDataSource({
    postProcess: this.agentsLoadPostProcess,
  });

  protected selectedCandidateSig = computed(
    () => {
      const formData = this.formDataSig();
      const candidate: ElasticSearchAgent = formData?.candidate;

      return candidate;
    },
    {
      equal: (prev, curr) => {
        return prev?.dbId === curr?.dbId;
      },
    },
  );

  protected selectedCandidateKeySig = computed(() => {
    const selectedCandidate = this.selectedCandidateSig();
    const dbId = selectedCandidate?.[BaseModelKeys.dbId];

    if (!dbId) {
      return [];
    }

    return [dbId];
  });

  protected selectedCandidateValidationResultsSig = computed(() => {
    const candidate = this.selectedCandidateSig();
    const agency = this.agencySig();

    const agencyId = agency?.[AgencyKeys.agencyId];
    const agencyType = agency?.[AgencyKeys.agencyType];
    const mgaId = agencyType === AGENCY_TYPE.MGA ? agencyId : agency?.[AgencyKeys.mga];
    const candidateAgentId = candidate?.[AgentKeys.p_agent_id];
    const candidateMgaId = candidate?.[AgentKeys.p_mga_id];
    const candidateAgencyId = candidate?.[AgentKeys.p_agency_id];
    const candidateAgentType = candidate?.[AgentKeys.agent_type];
    const isCandidateTeamLead = candidate?.[AgentKeys.is_manager];
    const isMgaMatch = mgaId === candidateMgaId;
    const isAgencyMatch = agencyId === candidateAgencyId;
    const isAgentTypeMathRequirements = new Set([
      AGENT_TYPE.AGENCY_ADMIN,
      AGENT_TYPE.AGENCY_PRINCIPAL,
      AGENT_TYPE.MGA_ADMIN,
      AGENT_TYPE.MGA_PRINCIPAL,
    ]).has(candidateAgentType);

    const validationIssues = [];

    if (!candidateAgentId) {
      validationIssues.push('This candidate must have Agent Id assigned.');
    }
    if (!isMgaMatch) {
      validationIssues.push('This candidate is not part of the required MGA.');
    }
    if (!isAgencyMatch) {
      validationIssues.push('This candidate is not part of the required Agency.');
    }
    if (!isCandidateTeamLead) {
      validationIssues.push('This candidate must be a Team Lead for this role.');
    }
    if (!isAgentTypeMathRequirements) {
      validationIssues.push('This candidate’s Agent Type doesn’t meet the position requirements.');
    }

    return validationIssues;
  });

  // protected selectedCandidateAgencyIdsSig = computed(
  //   () => {
  //     const formData = this.formDataSig();
  //     const candidate: ElasticSearchAgent = formData?.candidate;
  //     const mgaId = candidate?.[AgentKeys.p_mga_id];
  //     const agencyId = candidate?.[AgentKeys.p_agency_id];

  //     return {
  //       mgaId,
  //       agencyId,
  //     };
  //   },
  //   { equal: isEqual },
  // );

  // protected selectedCandidateAgencyDetails$ = toObservable(this.selectedCandidateAgencyIdsSig).pipe(
  //   mergeMap(selectedCandidateAgencyData => {
  //     const mgaId = selectedCandidateAgencyData?.mgaId;
  //     const agencyId = selectedCandidateAgencyData?.agencyId;

  //     return this.getSelectedCandidateAgencyDetails(mgaId, agencyId);
  //   }),
  // );

  // protected selectedCandidateAgencyDetailsSig = toSignal(this.selectedCandidateAgencyDetails$);

  private _dataLoadingStateSig = signal({});
  protected isDataLoadingSig = computed(() => {
    const dataLoadingState = this._dataLoadingStateSig();

    return Object.values(dataLoadingState ?? {})?.some(Boolean);
  });

  protected selectedCandidateMgaSig = signal(null);
  protected selectedCandidateAgencySig = signal(null);

  agencyNamesDataSourceConfiguration = this.agencyService.agencyNamesDataSourceConfiguration;
  agencies$ = this.agencyService.agencies$;
  mgasLookup$ = this.agencyService.mgasLookup$;

  constructor(
    private agencyService: AgencyService,
    private updateAgencyLeadModalService: UpdateAgencyLeadModalService,
    private agentSearchService: AgentSearchService,
  ) {}

  showModal = (): void => {
    this.updateAgencyLeadModalService.getFormData(this.agencySig());
    this.updateAgencyLeadModalModalComponent.showModal();
  };

  protected hasAgentIdValidationCallback = ({ value: candidate }) => {
    const candidateAgentId = candidate?.[AgentKeys.p_agent_id];

    return !!candidateAgentId;
  };

  protected isMgaMatchValidationCallback = ({ value: candidate }) => {
    const agency = this.agencySig();
    const agencyId = agency?.[AgencyKeys.agencyId];
    const agencyType = agency?.[AgencyKeys.agencyType];
    const candidateMgaId = candidate?.[AgentKeys.p_mga_id];
    const mgaId = agencyType === AGENCY_TYPE.MGA ? agencyId : agency?.[AgencyKeys.mga];
    const isMgaMatch = mgaId === candidateMgaId;

    return isMgaMatch;
  };

  protected isAgencyMatchValidationCallback = ({ value: candidate }) => {
    const agency = this.agencySig();
    const agencyId = agency?.[AgencyKeys.agencyId];
    const candidateAgencyId = candidate?.[AgentKeys.p_agency_id];
    const isAgencyMatch = agencyId === candidateAgencyId;

    return isAgencyMatch;
  };

  protected isCandidateTeamLeadValidationCallback = ({ value: candidate }) => {
    const isCandidateTeamLead = candidate?.[AgentKeys.is_manager];

    return isCandidateTeamLead;
  };

  protected isAgentTypeMatchRequirementsValidationCallback = ({ value: candidate }) => {
    const candidateAgentType = candidate?.[AgentKeys.agent_type];
    const isAgentTypeMathRequirements = new Set([
      AGENT_TYPE.AGENCY_ADMIN,
      AGENT_TYPE.AGENCY_PRINCIPAL,
      AGENT_TYPE.MGA_ADMIN,
      AGENT_TYPE.MGA_PRINCIPAL,
    ]).has(candidateAgentType);

    return isAgentTypeMathRequirements;
  };

  protected handleSave = async () => {
    const isValid = await validateDxGroups(this.validationGroup);

    if (!isValid) {
      return;
    }

    this.agencySig()[AgencyKeys.agencyLead] = this.formDataSig()?.candidate?.[AgentKeys.p_agent_id];

    this.updateAgencyLeadModalModalComponent.forceCloseModal();
    this.updateAgencyLeadModalService.formChangesDetector.clear();
  };

  protected handleClosePopup = this.updateAgencyLeadModalService.onCancelEdit;

  private agentsLoadPostProcess(response) {
    const data = response?.data?.map(agent => {
      const email = agent?.[AgentKeys.p_email];
      const agentName = agent?.[AgentKeys.p_agent_name];

      let fullName = '';

      if (agentName) {
        fullName += `${fullName} `;
      }

      if (email) {
        fullName += `(${email})`;
      }

      return Object.assign({}, agent, { [AgentKeys.p_agent_name]: fullName });
    });
    const totalCount = response?.totalCount;

    return { data, totalCount };
  }

  private getSelectedCandidateAgencyDetails = async (mgaId: string, agencyId: string) => {
    this._dataLoadingStateSig.update(state => {
      return Object.assign(state, {
        isMgaLoading: !!mgaId,
        isAgencyLoading: !!agencyId,
      });
    });
    const promises = [];

    if (mgaId) {
      const mgaQueries = [
        new QueryParam(AgencyKeys.agencyType, WhereFilterOperandKeys.equal, AGENCY_TYPE.MGA),
        new QueryParam(AgencyKeys.agencyId, WhereFilterOperandKeys.equal, mgaId),
      ];
      const mgaPromise = firstValueFrom(this.agencyService.getList(mgaQueries).pipe(take(1)));
      promises.push(mgaPromise);
    }

    if (agencyId && agencyId !== mgaId) {
      const agencyQueries = [new QueryParam(AgencyKeys.agencyId, WhereFilterOperandKeys.equal, agencyId)];
      const agencyPromise = firstValueFrom(this.agencyService.getList(agencyQueries).pipe(take(1)));
      promises.push(agencyPromise);
    }

    const response = await Promise.all(promises);

    this._dataLoadingStateSig.update(state => {
      return Object.assign(state, {
        isMgaLoading: false,
        isAgencyLoading: false,
      });
    });

    return response;
  };

  protected handleCandidateChange = (e: SelectionChangedEvent, dropDownBox: DxDropDownBoxComponent) => {
    const selectedCandidate = e?.selectedRowsData[0];

    this.formDataSig().candidate = selectedCandidate;
    validateDxGroups(this.validationGroup);

    dropDownBox.instance.close();
  };
}
